import React, { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Button from "../ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supplierActions from "src/redux/slices/supplierSlice";
import { Supplier } from "src/types/supplier.types";
import { toast } from "react-toastify";

interface AddSupplierModalProps {
  open: boolean;
  supplier?: Supplier | null;
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  gstNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
};

const EMPTY_VALUES: FormValues = {
  name: "",
  contactPerson: "",
  email: "",
  phone: "",
  gstNumber: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
};

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({
  open,
  supplier = null,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { createSupplier, editSupplier } = useAppSelector(
    (state) => state.supplier
  );

  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>(
    {}
  );

  useEffect(() => {
    setValues({
      name: supplier?.name || "",
      contactPerson: supplier?.contactPerson || "",
      email: supplier?.email || "",
      phone: supplier?.phone || "",
      gstNumber: supplier?.gstNumber || "",
      addressLine1: supplier?.addressLine1 || "",
      addressLine2: supplier?.addressLine2 || "",
      city: supplier?.city || "",
      state: supplier?.state || "",
      pincode: supplier?.pincode || "",
    });
    setErrors({});
  }, [supplier]);

  useEffect(() => {
    if (createSupplier.data) {
      toast.success("Supplier created successfully");
      dispatch(supplierActions.resetCreateSupplier());
      onSuccess();
    } else if (createSupplier.error) {
      toast.error(`Error: ${createSupplier.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSupplier]);

  useEffect(() => {
    if (editSupplier.data) {
      toast.success("Supplier updated successfully");
      dispatch(supplierActions.resetEditSupplier());
      onSuccess();
    } else if (editSupplier.error) {
      toast.error(`Error: ${editSupplier.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSupplier]);

  const handleChange = (field: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) newErrors.name = "Supplier name is required";
    if (!values.addressLine1.trim())
      newErrors.addressLine1 = "Address Line 1 is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (supplier?._id) {
      dispatch(
        supplierActions.editSupplierRequest({ ...values, _id: supplier._id })
      );
    } else {
      dispatch(supplierActions.createSupplierRequest(values));
    }
  };

  const loading = createSupplier.loading || editSupplier.loading;
  const title = supplier?._id ? "Edit Supplier" : "Add Supplier";

  return (
    <Modal open={open} onClose={onClose} title={title} size="2xl">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Basic details
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Supplier Name"
              required
              value={values.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
            <Input
              label="Contact Person"
              value={values.contactPerson}
              onChange={(e) => handleChange("contactPerson", e.target.value)}
            />
            <Input
              label="Phone"
              value={values.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              value={values.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            <Input
              label="GST Number"
              value={values.gstNumber}
              onChange={(e) => handleChange("gstNumber", e.target.value)}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Address
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Address Line 1"
              required
              value={values.addressLine1}
              onChange={(e) => handleChange("addressLine1", e.target.value)}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1}
            />
            <Input
              label="Address Line 2"
              optional
              value={values.addressLine2}
              onChange={(e) => handleChange("addressLine2", e.target.value)}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="City"
                value={values.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
              <Input
                label="State"
                value={values.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
              <Input
                label="Pincode"
                value={values.pincode}
                onChange={(e) => handleChange("pincode", e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={loading} disabled={loading}>
            {supplier?._id ? "Save changes" : "Add supplier"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSupplierModal;
